import { keepCaptionsVisibility } from '@src/services/karaoke/captionsVisibility';
import { GenericPresetTemplates } from '@src/types.ts/general';
import {
  isOriginalLang,
  ORIGINAL_LANGUAGE,
} from '@src/utility/captionLanguage';

import { getCaptionFontCorrection } from './captionFontSubstitution';

import type { IVideoEditor } from '../interface/IVideoEditor';
import type { KaraokeConfig as KaraokeConfigWithId } from '../models/karaoke/KaraokeConfig';
import type { CaptionFontSubstitution } from './captionFontSubstitution';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

/**
 * Optional custom-template seed for the translate flow (PLAT-10635). When the
 * clip's captions are still the built-in "Impact" default (no
 * `parent_template_id`, or the default preset), `applyCaptionsLanguage` swaps
 * in this template's style instead of re-producing "Impact". A user-picked
 * template (real `parent_template_id`) is left untouched.
 */
export type CaptionFallbackTemplate = {
  config: KaraokeConfig;
  templateId: string;
};

/**
 * Merge a fallback custom-template's style over a karaoke config that is still
 * on the built-in "Impact" default, preserving the config's `id` and
 * `language`. Returns `null` when the config already carries a user-picked
 * template (a real `parent_template_id`) — the caller must then keep the
 * existing style. Shared by `applyCaptionsLanguage` and the Translation Review
 * player so both seed the same way (PLAT-10635).
 */
export function seedFallbackTemplate(
  config: KaraokeConfigWithId,
  fallbackTemplate: CaptionFallbackTemplate,
): KaraokeConfigWithId | null {
  const isBuiltInDefault =
    !config.parent_template_id ||
    config.parent_template_id === GenericPresetTemplates.default;
  if (!isBuiltInDefault) return null;
  // PLAT-11689 — a style seed never changes whether captions are shown.
  return keepCaptionsVisibility(config, {
    ...fallbackTemplate.config,
    id: config.id,
    language: config.language,
    parent_template_id: fallbackTemplate.templateId,
  });
}

/**
 * Prepare the editor's current default karaoke config for a first produce that
 * does NOT go through `applyCaptionsLanguage` — the CAPTIONS button
 * (Transcript) and the Translation Review player — and persist it (no undo
 * entry):
 *
 *  1. Seed the fallback custom template over the built-in default (PLAT-10635),
 *     unless the clip already carries a user-picked template (see
 *     {@link seedFallbackTemplate}). Skipped without a fallback.
 *  2. PLAT-11674 — when `opts.targetLanguageKey` (the CANONICAL caption
 *     language key, see `resolveCaptionLanguageKey`) is given, run the
 *     PLAT-11409 font correction on the (possibly just-seeded) config, so a
 *     first produce in a non-Latin language can't ship tofu. Without it these
 *     surfaces produced Anton + Japanese — the correction only ran on the
 *     language-SWITCH path, which a captionless video never takes.
 *
 * Returns the font substitution made, if any, for the caller's passive notice
 * (`notifyCaptionFontChange`). No-op when there is no default config or nothing
 * needs to change.
 */
export function seedFallbackTemplateOnEditor(
  editor: IVideoEditor,
  fallbackTemplate: CaptionFallbackTemplate | undefined,
  opts?: { targetLanguageKey?: string },
): CaptionFontSubstitution | undefined {
  const config = editor.getDefaultKaraokeConfig();
  if (!config) return undefined;
  const seeded = fallbackTemplate
    ? seedFallbackTemplate(config, fallbackTemplate)
    : null;
  const base = seeded ?? config;
  const correction = opts?.targetLanguageKey
    ? getCaptionFontCorrection(base, opts.targetLanguageKey)
    : null;
  if (!seeded && !correction) return undefined;
  editor.commandUpdateKaraokeConfig(
    correction ? { ...base, ...correction.fields } : base,
    false,
  );
  return correction?.substitution;
}

/**
 * PLAT-11877 — put a caption template's style on the editor's default config,
 * whatever it carried before (unlike the seed above, which yields to a
 * user-picked template): the Captions picker's template rows beside the
 * player. Preserves the config's `id`, `language` and visibility, records the
 * template as `parent_template_id`, and runs the same font correction the
 * seed runs for the caption language. Carries the undo entry, so the hosts
 * snapshot before the style changes; the caller's produce must not take a
 * second one. Returns the font substitution made, if any.
 */
export function applyCaptionTemplateOnEditor(
  editor: IVideoEditor,
  template: CaptionFallbackTemplate,
  opts?: { targetLanguageKey?: string },
): CaptionFontSubstitution | undefined {
  const config = editor.getDefaultKaraokeConfig();
  if (!config) return undefined;
  const next = keepCaptionsVisibility(config, {
    ...template.config,
    id: config.id,
    language: config.language,
    parent_template_id: template.templateId,
  });
  const correction = opts?.targetLanguageKey
    ? getCaptionFontCorrection(next, opts.targetLanguageKey)
    : null;
  editor.commandUpdateKaraokeConfig(
    correction ? { ...next, ...correction.fields } : next,
  );
  return correction?.substitution;
}

/**
 * Outcome of a caption-language switch attempt.
 *
 *   - `noop`        — the requested language already matches the current
 *                     `KaraokeConfig.language` (treating 'original' and
 *                     'english' as equivalent). Caller should not refresh
 *                     or re-render.
 *   - `applied`     — config updated and karaoke track re-produced
 *                     successfully. Caller MUST surface the result to its
 *                     observable layer (clip preview: `refreshKaraokeState`;
 *                     Creator Studio: `editorStore.refreshKaraoke`).
 *   - `no-config`   — no default karaoke config exists yet; nothing to
 *                     mutate. Typically pre-load.
 *   - `no-track`    — no karaoke track currently exists (the user cleared
 *                     captions, or none has ever been produced). Nothing is
 *                     produced — a language change must not resurrect a deleted
 *                     caption track — but the requested language IS persisted
 *                     onto the config so the eventual first produce renders it
 *                     (see the `hasKaraokeTrack` guard below). Caller should not
 *                     refresh or re-render.
 *   - `produce-failed` — `commandProduceKaraoke` returned false (e.g. no
 *                     active transcript tokens). Config has been mutated
 *                     to the requested language but no track was produced;
 *                     editor is in a partially-applied state. Caller
 *                     should log loudly.
 *   - `exception`   — one of the commands threw. Same partial-state caveat
 *                     as `produce-failed`.
 *
 * Rollback on failure is intentionally NOT performed here, mirroring
 * Creator Studio's pre-existing flow: a recoverable partial state is
 * preferable to attempting a rollback that may itself fail mid-way.
 * Callers are expected to log and let the user re-pick.
 */
export type ApplyCaptionsLanguageResult =
  | { kind: 'noop'; currentLang: string }
  | {
      kind: 'applied';
      currentLang: string;
      nextLang: string;
      /** PLAT-11409 (DE1) — set when the caption font was auto-swapped to a
       *  script-capable equivalent for the target language, so callers can
       *  surface the passive notice. */
      fontSubstituted?: CaptionFontSubstitution;
    }
  | { kind: 'no-config' }
  | { kind: 'no-track'; currentLang: string }
  | { kind: 'produce-failed'; currentLang: string; nextLang: string }
  | {
      kind: 'exception';
      currentLang: string;
      nextLang: string;
      error: unknown;
    };

/**
 * Update the editor's default `KaraokeConfig.language` and re-produce the
 * karaoke track so the rendered captions follow the new language.
 *
 * Pure orchestration over editor commands — caller wires its own observable
 * refresh + any surface-specific side effects (Creator Studio dispatches a
 * window event and forces showSubtitles=true; the clip-preview bridge
 * doesn't).
 *
 * Idempotency: returns `{ kind: 'noop' }` when the requested language
 * already matches the current config, with both 'original' and 'english'
 * treated as the same bucket.
 *
 * PLAT-10816 — `opts.createUndoRedoEntry` marks a user's own pick; automatic
 * callers leave it `false`. It rides the config command only, mirroring
 * `useKaraoke.updateKaraokeConfigAndProduce`: snapshots are taken *before* each
 * command, so an entry on the re-produce would restore the unreachable state
 * between the two (new language, old track). In the clip preview the same flag
 * gates the adapter's autosave, so `false` here means the switch never reaches
 * storage — don't "simplify" it away.
 */
export function applyCaptionsLanguage(
  editor: IVideoEditor,
  next: string | null,
  fallbackTemplate?: CaptionFallbackTemplate,
  opts?: {
    createUndoRedoEntry?: boolean;
    /**
     * PLAT-11409 — the CANONICAL caption-language key (from
     * `resolveCaptionLanguageKey`) used for font substitution. Defaults to
     * `next`, under which the `'original'`/`'english'` sentinels resolve to
     * Latin (= no substitution); callers that know the resolved key pass it
     * so a non-Latin ORIGINAL language corrects too (see
     * `switchCaptionLanguage`).
     */
    targetLanguageKey?: string;
    /**
     * PLAT-11416 — the org's curated per-language template variant for the
     * (possibly just-seeded) config's template family, keyed by the resolved
     * `targetLanguageKey`. A hit replaces the produced config wholesale (the
     * variant IS the org's look for that language); the font correction
     * still runs on top, so a curated Latin-only font can't ship tofu.
     * Injected as a callback to keep this editor service free of org/store
     * imports.
     */
    resolveLanguageVariant?: (
      parentTemplateId: string,
      languageKey: string,
    ) => CaptionFallbackTemplate | null;
  },
): ApplyCaptionsLanguageResult {
  const config = editor.getDefaultKaraokeConfig();
  if (!config) return { kind: 'no-config' };

  const nextLang = next ?? ORIGINAL_LANGUAGE;
  const currentLang = config.language || ORIGINAL_LANGUAGE;

  if (
    nextLang === currentLang ||
    (isOriginalLang(nextLang) && isOriginalLang(currentLang))
  ) {
    return { kind: 'noop', currentLang };
  }

  // Don't resurrect a deleted caption track. If the user cleared captions
  // (removed the karaoke track), a language change — e.g. from the transcript
  // language picker — must only switch the language of an EXISTING track, not
  // silently re-produce one. First-time production goes through the CAPTIONS
  // button / template apply, never here.
  //
  // PLAT-10788 follow-up: still persist the requested language onto the config.
  // Writing the language field resurrects nothing (no track is created), but it
  // is the ONLY record of the user's pick that the first-produce paths read —
  // `ProduceKaraokeCommand` routes translated-vs-original purely off
  // `KaraokeConfig.language`. Returning early without it left a video whose
  // captions had never been produced stuck on `'original'`: picking a
  // translation and then pressing CAPTIONS (or applying a caption template)
  // produced source-language captions, which `KaraokeStore`'s per-language
  // write-back then persisted under the *selected* language's key.
  if (!editor.hasKaraokeTrack()) {
    editor.commandUpdateKaraokeConfig({ ...config, language: nextLang }, false);
    return { kind: 'no-track', currentLang };
  }

  // PLAT-10635: if the clip is still on the built-in "Impact" default and the
  // org has a saved custom template, seed the new-language captions from that
  // template instead of Impact. A user-picked template is left as-is.
  const seeded = fallbackTemplate
    ? seedFallbackTemplate(config, fallbackTemplate)
    : null;
  let base = seeded
    ? { ...seeded, language: nextLang }
    : { ...config, language: nextLang };
  // PLAT-11416 — an org-curated variant of this template family for the
  // target language outranks both the carried-over config and the neutral
  // seed. Resolved off the post-seed parent so a just-seeded fallback
  // template's family is consulted too.
  const variantParentId = base.parent_template_id;
  const languageVariant =
    variantParentId && opts?.targetLanguageKey && opts.resolveLanguageVariant
      ? opts.resolveLanguageVariant(variantParentId, opts.targetLanguageKey)
      : null;
  if (languageVariant) {
    // Nor show or hide them (the Cap toggle's state, PLAT-11689).
    base = keepCaptionsVisibility(base, {
      ...languageVariant.config,
      id: config.id,
      language: nextLang,
      parent_template_id: languageVariant.templateId,
      // `locked` lives on the config precisely so it survives regeneration
      // (see KaraokeConfig) — a language switch must not unlock captions.
      locked: base.locked,
    });
  }
  // PLAT-11409 (DE1) — after seeding, swap in the script-equivalent font when
  // the (possibly just-seeded) config's font can't render the target
  // language. Runs on the seeded template's font on purpose (T2 DS5): an org
  // template gets the same anti-tofu guarantee as the built-in default.
  const correction = getCaptionFontCorrection(
    base,
    opts?.targetLanguageKey ?? nextLang,
  );
  const nextConfig = correction ? { ...base, ...correction.fields } : base;

  try {
    editor.commandUpdateKaraokeConfig(
      nextConfig,
      opts?.createUndoRedoEntry ?? false,
    );
    const produced = editor.commandProduceKaraoke({
      replaceTrack: true,
      createUndoRedoEntry: false,
    });
    if (!produced) {
      return { kind: 'produce-failed', currentLang, nextLang };
    }
    return {
      kind: 'applied',
      currentLang,
      nextLang,
      fontSubstituted: correction?.substitution,
    };
  } catch (error) {
    return { kind: 'exception', currentLang, nextLang, error };
  }
}
